import React, { useState, useEffect, memo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Typography, Grid, Chip, Box, Button, Skeleton, Container, Tooltip, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CreateIcon from '@mui/icons-material/Create';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import Expandable from '../components/common/Expandable';
import WikipediaViewer from '../components/WikipediaViewer';
import { getMovieDetails, getMovieStatus, MovieStatusResponse } from '../services/movieApi';
import { MovieDetail, MovieReviewData, MovieCastMember, MovieCrewMember, MovieVideo } from '../types/Movie';
import YoutubeViewer from '../components/video/YoutubeViewer';
import MovieStatusButton from '../components/MovieStatusButton';
import MovieReviewDialog from '../components/MovieReviewDialog';
import { useUser } from '../contexts/UserContext';
import MovieScoresSection from '../components/MovieScoresSection';

// ============================================================
// Crew Job Translation
// ============================================================
const CREW_JOB_JA: Record<string, string> = {
  'Director': '監督',
  'Writer': '原案・脚本',
  'Screenplay': '脚本',
  'Producer': 'プロデューサー',
  'Executive Producer': 'エグゼクティブプロデューサー',
  'Original Music Composer': '音楽',
  'Director of Photography': '撮影監督',
  'Editor': '編集',
  'Production Design': '美術',
  'Costume Design': '衣装',
  'Casting': 'キャスティング',
  'Sound Designer': '音響',
  'Visual Effects Supervisor': 'VFXスーパーバイザー',
  'Stunt Coordinator': 'スタントコーディネーター',
  'Co-Producer': '共同プロデューサー',
  'Associate Producer': 'アソシエイトプロデューサー',
  'Line Producer': 'ラインプロデューサー',
  'Story': '原案',
  'Original Story': '原作',
  'Novel': '原作小説',
  'Characters': 'キャラクター原案',
  'Comic Book': '原作コミック',
  'Art Direction': '美術監督',
  'Set Decoration': '装飾',
  'Makeup Artist': 'メイク',
  'Hairstylist': 'ヘアスタイリスト',
  'Music Supervisor': '音楽監修',
  'Sound Mixer': 'サウンドミキサー',
  'Special Effects': '特殊効果',
};
const translateJob = (job: string): string => CREW_JOB_JA[job] || job;

// ============================================================
// Styled Components (AnimeDetailsPage準拠)
// ============================================================

const FullWidthGlassSection = styled('div')({
  position: 'relative',
  width: '100vw',
  left: '50%',
  right: '50%',
  marginTop: '-240px',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  paddingTop: '64px',
  zIndex: 1,
  '@media (max-width: 899px)': {
    marginTop: '-164px',
    paddingTop: '56px',
  }
});

const BackgroundImage = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(35px)',
  opacity: 0.1,
  zIndex: -1,
}));

const GlassOverlay = styled('div')({
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(18, 18, 18, 0)',
  zIndex: -1,
});

const ContentWrapper = styled(Container)({
  position: 'relative',
  zIndex: 1,
  overflow: 'visible',
  '@media (max-width: 899px)': {
    paddingTop: '0 !important'
  }
});

const NormalSection = styled('div')({
  position: 'relative',
  zIndex: 2,
  backgroundColor: '#121212',
});

const HeroImageSection = styled('div')({
  position: 'relative',
  width: '100%',
  height: '464px',
  marginTop: '-64px',
  marginBottom: '-100px',
  overflow: 'hidden',
  '@media (max-width: 899px)': {
    height: '400px',
    marginTop: '-56px',
    marginBottom: '-60px',
  }
});

const HeroGradient = styled('div')({
  position: 'absolute',
  bottom: 0, left: 0, right: 0,
  height: '200px',
  background: 'linear-gradient(to bottom, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 0.85) 70%, rgba(18, 18, 18, 1) 100%)',
  pointerEvents: 'none',
});

const HeroFallbackGradient = styled('div')({
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'linear-gradient(135deg, #1a237e 0%, #121212 100%)',
  opacity: 0.5,
});

const HeroBackdrop = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center 25%',
  opacity: 0.6,
}));

const HeroBlurredCover = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(25px)',
  opacity: 0.35,
}));

const CoverImageSection = styled('div')({
  position: 'relative',
  width: '100%',
  maxWidth: '400px',
  marginBottom: '0.5rem',
  '@media (max-width: 899px)': {
    width: '35%',
    minWidth: '120px',
    maxWidth: '120px',
    marginTop: '-80px',
    marginLeft: '0px',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
  }
});

const CoverImage = styled('img')({
  width: '100%',
  height: 'auto',
  display: 'block',
  borderRadius: '4px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  '@media (max-width: 899px)': {
    aspectRatio: '3/4',
    objectFit: 'cover',
  }
});

const StatusButtonsContainer = styled(Box)({
  marginTop: '8px',
  '@media (max-width: 899px)': {
    position: 'absolute',
    right: '-120px',
    bottom: '0',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    width: '110px'
  }
});

const LoginPrompt = styled(Link)({
  fontSize: '0.7rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginTop: '8px',
  textAlign: 'center',
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  display: 'block',
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  '@media (max-width: 899px)': {
    fontSize: '0.6rem',
    position: 'absolute',
    textAlign: 'left',
    lineHeight: '1.2',
    right: '-210px',
    bottom: '0',
    width: '200px',
    marginTop: 0
  }
});

// ============================================================
// Helper Components
// ============================================================

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" component="h2" sx={{
    fontSize: { xs: '0.75rem', sm: '0.8rem' },
    fontWeight: 600,
    mb: 1,
    color: '#ffffff',
  }}>
    {children}
  </Typography>
);

const MetaChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      fontSize: { xs: '0.6rem', sm: '0.65rem' },
      height: { xs: '20px', sm: '22px' },
      borderRadius: '4px',
      bgcolor: 'rgba(255, 255, 255, 0.08)',
      color: '#ffffffcc',
    }}
  />
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
    <Typography variant="caption" sx={{ color: '#a7a7a7', minWidth: '60px', fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ color: '#ffffffcc', fontSize: '0.65rem' }}>
      {value}
    </Typography>
  </Box>
);

const formatRuntime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h${m}min`;
  return `${m}min`;
};

const languageMap: Record<string, string> = {
  en: '英語', ja: '日本語', ko: '韓国語', zh: '中国語', fr: 'フランス語',
  de: 'ドイツ語', es: 'スペイン語', it: 'イタリア語', pt: 'ポルトガル語',
  ru: 'ロシア語', hi: 'ヒンディー語', th: 'タイ語', id: 'インドネシア語',
  ar: 'アラビア語', tr: 'トルコ語', pl: 'ポーランド語', sv: 'スウェーデン語',
  da: 'デンマーク語', no: 'ノルウェー語', fi: 'フィンランド語', nl: 'オランダ語',
  cs: 'チェコ語', hu: 'ハンガリー語', ro: 'ルーマニア語', el: 'ギリシャ語',
  he: 'ヘブライ語', vi: 'ベトナム語', ms: 'マレー語', tl: 'タガログ語',
  uk: 'ウクライナ語', bn: 'ベンガル語', ta: 'タミル語', te: 'テルグ語',
  ml: 'マラヤーラム語', cn: '広東語',
};

const formatCurrency = (amount: number): string => {
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount}`;
};

// ============================================================
// Main Component
// ============================================================

const MovieDetailsPage: React.FC = () => {
  const { movie_id } = useParams<{ movie_id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useUser();
  const [movie, setMovie] = useState<MovieDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState<number | null>(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState<MovieReviewData>({
    score: null, reviewText: null, scoreTags: [],
    scoreStory: null, scoreVisual: null, scoreSound: null,
    scoreActing: null, scoreDirection: null,
    watchMedium: [], watchStyle: [],
    startDate: null, endDate: null, rewatch: false,
  });
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [wikiSummary, setWikiSummary] = useState<string | null>(null);
  const [wikiOpen, setWikiOpen] = useState(false);
  const [hasWikiData, setHasWikiData] = useState(false);

  const buildReviewData = (s: MovieStatusResponse): MovieReviewData => ({
    score: s.score,
    reviewText: s.review_text,
    scoreTags: s.score_tags || [],
    scoreStory: s.score_story,
    scoreVisual: s.score_visual,
    scoreSound: s.score_sound,
    scoreActing: s.score_acting,
    scoreDirection: s.score_direction,
    watchMedium: s.watch_medium || [],
    watchStyle: s.watch_style || [],
    startDate: s.start_date,
    endDate: s.end_date,
    rewatch: s.rewatch,
  });

  useEffect(() => {
    if (userLoading) return;

    const fetchMovieDetails = async () => {
      setLoading(true);
      setMovie(null);
      setUserStatus(null);
      try {
        if (movie_id) {
          const data = await getMovieDetails(parseInt(movie_id, 10));
          setMovie(data);

          // ログイン中ならユーザーステータス取得
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const statusData = await getMovieStatus(parseInt(movie_id, 10));
              setUserStatus(statusData.status);
              setReviewData(buildReviewData(statusData));
            } catch {}
          }
        }
      } catch (error) {
        console.error('映画詳細の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMovieDetails();
    window.scrollTo(0, 0);
  }, [movie_id, userLoading]);

  // Wikipedia概要取得
  useEffect(() => {
    if (movie) {
      const fetchWikiSummary = async () => {
        try {
          const response = await fetch(`https://ja.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(movie.japanese_name || movie.title)}`);
          const data = await response.json();
          if (data.extract) {
            setWikiSummary(data.extract);
            setHasWikiData(true);
          } else {
            setHasWikiData(false);
          }
        } catch (error) {
          console.error('Wikipedia APIからのデータ取得に失敗しました:', error);
          setHasWikiData(false);
        }
      };
      fetchWikiSummary();
    }
  }, [movie]);

  // ============================================================
  // Loading
  // ============================================================
  if (loading) {
    return (
      <>
        <HeroImageSection>
          <HeroFallbackGradient />
          <HeroGradient />
        </HeroImageSection>
        <FullWidthGlassSection>
          <ContentWrapper maxWidth="lg" sx={{ px: 1.5, pt: { xs: 2, md: 4 }, pb: 4 }}>
            <Grid container spacing={{ xs: 2, md: 4 }}>
              <Grid item xs={12} md={3.5}>
                <CoverImageSection>
                  <Skeleton variant="rectangular" sx={{ width: '100%', height: 0, paddingBottom: '133.33%', borderRadius: '4px' }} />
                </CoverImageSection>
              </Grid>
              <Grid item xs={12} md={8.5}>
                <Skeleton width="80%" height={32} />
                <Skeleton width="60%" height={24} sx={{ mt: 1 }} />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Skeleton width={60} height={24} />
                  <Skeleton width={60} height={24} />
                  <Skeleton width={60} height={24} />
                </Box>
                <Skeleton width="100%" height={100} sx={{ mt: 2 }} />
              </Grid>
            </Grid>
          </ContentWrapper>
        </FullWidthGlassSection>
        <NormalSection>
          <Container maxWidth="lg" sx={{ px: 1.5, pt: 2, pb: 4, minHeight: '50vh' }}>
            <Skeleton width="100%" height={200} />
          </Container>
        </NormalSection>
      </>
    );
  }

  if (!movie) {
    return (
      <Container maxWidth="lg" sx={{ pt: 10, textAlign: 'center' }}>
        <Typography>映画情報が見つかりません。</Typography>
      </Container>
    );
  }

  // ============================================================
  // Data
  // ============================================================
  const displayTitle = movie.japanese_name || movie.title;
  const subTitle = movie.japanese_name
    ? (movie.original_title !== movie.japanese_name ? movie.original_title : movie.title)
    : movie.original_title;
  const imageUrl = movie.poster_url || '/image/no_image.png';
  const releaseYear = movie.release_date ? new Date(movie.release_date).getFullYear() : null;

  // Director & Writers from crew
  const directors = movie.crew.filter(c => c.jobs.includes('Director'));
  const writers = movie.crew.filter(c => c.jobs.some(j => ['Writer', 'Screenplay'].includes(j)));
  const originalCreators = movie.crew.filter(c => c.jobs.some(j => ['Story', 'Original Story', 'Novel', 'Comic Book', 'Characters'].includes(j)));

  // 動画 (Trailer/Teaser優先)
  const allVideos = movie.videos.slice(0, 4);

  return (
    <>
      <Helmet>
        <title>
          {movie.japanese_name
            ? `${movie.japanese_name} / ${movie.title}${releaseYear ? ` (${releaseYear})` : ''} | PULSE`
            : `${movie.title}${releaseYear ? ` (${releaseYear})` : ''} | PULSE`
          }
        </title>
      </Helmet>

      {/* ============================================================ */}
      {/* Hero Section */}
      {/* ============================================================ */}
      <HeroImageSection>
        {movie.backdrop_url ? (
          <>
            <HeroBackdrop image={movie.backdrop_url} />
            <HeroGradient />
          </>
        ) : movie.poster_url ? (
          <>
            <HeroBlurredCover image={movie.poster_url} />
            <HeroGradient />
          </>
        ) : (
          <>
            <HeroFallbackGradient />
            <HeroGradient />
          </>
        )}
      </HeroImageSection>

      {/* ============================================================ */}
      {/* Glass Section - Main Info */}
      {/* ============================================================ */}
      <FullWidthGlassSection>
        <BackgroundImage image={imageUrl} />
        <GlassOverlay />
        <ContentWrapper maxWidth="lg" sx={{
          px: 1.5,
          pt: { xs: 2, md: 4 },
          pb: { xs: 2, md: 4 }
        }}>
          <Grid container spacing={{ xs: 2, md: 4 }} sx={{
            '@media (max-width: 899px)': { flexDirection: 'column' }
          }}>
            {/* Left: Cover Image */}
            <Grid item xs={12} md={3.5} sx={{
              '@media (max-width: 899px)': { flex: '0 0 auto', maxWidth: '100%', paddingTop: 0 }
            }}>
              <CoverImageSection>
                <CoverImage src={imageUrl} alt={displayTitle} />
                <StatusButtonsContainer sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  '& > *': { flexShrink: 0 },
                  '@media (max-width: 899px)': {
                    marginBottom: !user ? '48px' : 0
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <MovieStatusButton
                      movieId={movie.movie_id}
                      initialStatus={userStatus}
                      onStatusChange={(newStatus) => setUserStatus(newStatus)}
                    />
                    {userStatus && [1, 2, 3].includes(userStatus) && (
                      <Tooltip title="レビューを書く" arrow>
                        <IconButton
                          onClick={() => setOpenReviewDialog(true)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '12px',
                            padding: { xs: '6px 12px', sm: '8px 12px' },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <CreateIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </StatusButtonsContainer>
                {!user && (
                  <LoginPrompt to="/login">
                    ログインして映画を記録・管理しよう
                  </LoginPrompt>
                )}
              </CoverImageSection>
            </Grid>

            {/* Right: Metadata */}
            <Grid item xs={12} md={6.85} sx={{
              '@media (max-width: 899px)': { paddingTop: 0 }
            }}>
              {/* Title */}
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '0.9rem', sm: '1.3rem' } }}>
                {displayTitle}
                {releaseYear && (
                  <Typography component="span" color="text.secondary" sx={{ ml: 1.5, fontSize: '1em' }}>
                    ({releaseYear})
                  </Typography>
                )}
              </Typography>
              {subTitle && subTitle !== displayTitle && (
                <Typography variant="h6" component="h2" sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  lineHeight: 1.2,
                  mb: 1
                }}>
                  {subTitle}
                </Typography>
              )}

              {/* Genre (dot-separated) */}
              {movie.genres.length > 0 && (
                <Typography variant="body2" sx={{
                  color: '#a7a7a7',
                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                  mb: 1,
                  '& > span::before': { content: '"・"', color: 'rgba(255, 255, 255, 0.3)' }
                }}>
                  {movie.genres.map((g) => (
                    <Box component="span" key={g.genre_id}>
                      <Link to={`/movie/genre/${g.genre_id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {g.japanese_name || g.name}
                      </Link>
                    </Box>
                  ))}
                </Typography>
              )}

              {/* Meta Chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {!!movie.runtime && movie.runtime > 0 && <MetaChip label={formatRuntime(movie.runtime)} />}
                {movie.certification && <MetaChip label={movie.certification} />}
                {(() => {
                  if (!movie.release_date) return null;
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return new Date(movie.release_date) > today ? <MetaChip label="公開前" /> : null;
                })()}
                {movie.origin_countries && movie.origin_countries.length > 0 && (
                  movie.origin_countries.map((c) => (
                    <MetaChip key={c.iso_3166_1} label={c.name} />
                  ))
                )}
              </Box>

              {/* summary優先、なければWikipedia */}
              {(movie.summary || wikiSummary) && (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <Expandable maxLines={5} expandButtonStyle="icon">
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      lineHeight: 1.25,
                      margin: 0,
                    }}>
                      {movie.summary || wikiSummary}
                      {hasWikiData && (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            ml: 0.5,
                            fontSize: 'inherit',
                            verticalAlign: 'middle',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => setWikiOpen(true)}
                        >
                          <AutoStoriesIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.7rem', verticalAlign: 'middle' }} />
                          Viewerで見る
                        </Typography>
                      )}
                    </Typography>
                  </Expandable>
                </Box>
              )}

              {/* Info Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                      監督
                    </Typography>
                    {directors.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {directors.map((d) => (
                          <Chip
                            key={d.person_id}
                            component={Link}
                            to={`/movie/person/${d.person_id}`}
                            label={d.name}
                            clickable
                            size="small"
                            sx={{
                              fontSize: { xs: '0.675rem', sm: '0.7rem' },
                              height: { xs: '20px', sm: '24px' },
                              bgcolor: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                              textDecoration: 'none',
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.675rem', color: '#a7a7a7' }}>情報なし</Typography>
                    )}
                  </Box>
                </Grid>
                {writers.length > 0 && (
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                        脚本
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {writers.map((w) => (
                          <Chip
                            key={w.person_id}
                            component={Link}
                            to={`/movie/person/${w.person_id}`}
                            label={w.name}
                            clickable
                            size="small"
                            sx={{
                              fontSize: { xs: '0.675rem', sm: '0.7rem' },
                              height: { xs: '20px', sm: '24px' },
                              bgcolor: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                              textDecoration: 'none',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
                {originalCreators.length > 0 && (
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                        原作・原案
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {originalCreators.map((c) => (
                          <Chip
                            key={c.person_id}
                            component={Link}
                            to={`/movie/person/${c.person_id}`}
                            label={c.name}
                            clickable
                            size="small"
                            sx={{
                              fontSize: { xs: '0.675rem', sm: '0.7rem' },
                              height: { xs: '20px', sm: '24px' },
                              bgcolor: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                              textDecoration: 'none',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                      制作会社
                    </Typography>
                    {movie.companies.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {movie.companies.slice(0, 3).map((c) => (
                          <Chip
                            key={c.company_id}
                            component={Link}
                            to={`/movie/company/${c.company_id}`}
                            label={c.japanese_name || c.name}
                            clickable
                            size="small"
                            sx={{
                              fontSize: { xs: '0.675rem', sm: '0.7rem' },
                              height: { xs: '20px', sm: '24px' },
                              bgcolor: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                              textDecoration: 'none',
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.675rem', color: '#a7a7a7' }}>情報なし</Typography>
                    )}
                  </Box>
                </Grid>
                {movie.collection && (
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                        シリーズ
                      </Typography>
                      <Chip
                        component={Link}
                        to={`/movie/collection/${movie.collection.collection_id}`}
                        label={movie.collection.name.replace(/\s*(Collection|シリーズ)\s*$/i, '').trim()}
                        clickable
                        size="small"
                        sx={{
                          fontSize: { xs: '0.675rem', sm: '0.7rem' },
                          height: { xs: '20px', sm: '24px' },
                          bgcolor: 'rgba(255, 255, 255, 0.08)',
                          color: '#ffffff',
                          width: 'fit-content',
                          textDecoration: 'none',
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Release Info */}
              <Box sx={{ mt: 2 }}>
                {movie.release_date && (
                  <InfoRow label="公開日" value={new Date(movie.release_date).toLocaleDateString('ja-JP')} />
                )}
              </Box>
            </Grid>
          </Grid>
        </ContentWrapper>
      </FullWidthGlassSection>

      <NormalSection>
        <Container maxWidth="lg" sx={{ px: 1.5, pt: { xs: 3, md: 4 }, pb: 4 }}>
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={8}>

              {/* 概要 (TMDB) */}
              {(movie.tagline_ja || movie.overview_ja || movie.overview) && (
                <Box sx={{ mb: 2 }}>
                  <SectionTitle>概要</SectionTitle>
                  <Expandable maxHeight={250} expandButtonStyle="pill" pillTextAlign="right">
                    {movie.tagline_ja && (
                      <Typography variant="body1" component="h3" sx={{ fontSize: '0.85rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.5)', mb: 1.5, lineHeight: 1.25 }}>
                        {movie.tagline_ja}
                      </Typography>
                    )}
                    {(movie.overview_ja || movie.overview) && (
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.35 }}>
                        {movie.overview_ja || movie.overview}
                      </Typography>
                    )}
                  </Expandable>
                </Box>
              )}

              {/* Videos */}
              {allVideos.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>動画</SectionTitle>
                  <Grid container spacing={1.5}>
                    {allVideos.map((video, idx) => (
                      <Grid item xs={6} sm={6} key={idx}>
                        <Box sx={{ mb: 1 }}>
                          <Box
                            onClick={() => setSelectedVideoId(video.video_key)}
                            sx={{
                              position: 'relative',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                '& .overlay': { opacity: 1 },
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={`https://img.youtube.com/vi/${video.video_key}/hqdefault.jpg`}
                              alt={video.name || '動画'}
                              sx={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
                            />
                            <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)', opacity: 0, transition: 'opacity 0.2s ease-in-out', zIndex: 1 }} />
                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                              <PlayArrowIcon sx={{ fontSize: { xs: 28, md: 40 }, color: '#fff' }} />
                            </Box>
                          </Box>
                          {video.name && (
                            <Typography variant="caption" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                              {video.name}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* 評価スコア */}
              <MovieScoresSection
                tmdbVoteAverage={movie.vote_average ?? null}
                tmdbVoteCount={movie.vote_count ?? null}
              />

              {/* Cast */}
              {movie.cast.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>キャスト</SectionTitle>
                  {user?.is_superuser ? (
                    <Grid container spacing={1}>
                      {movie.cast.map((c) => (
                        <Grid item xs={3} sm={2.4} md={2} key={c.person_id}>
                          <Box component="img"
                            src={c.profile_url || '/image/no_image.png'}
                            alt={c.name}
                            sx={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block', borderRadius: '4px' }}
                          />
                          <Typography variant='body2' component="span" noWrap sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#ffffff', mt: 0.5, lineHeight: 1.3, display: 'block' }}>
                            {c.name}
                          </Typography>
                          {c.character_name && (
                            <Typography variant='caption' component="span" noWrap sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.2, display: 'block' }}>
                              {c.character_name}
                            </Typography>
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      準備中
                    </Typography>
                  )}
                </Box>
              )}

              {/* Crew */}
              {movie.crew.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>スタッフ</SectionTitle>
                  {user?.is_superuser ? (
                    <Grid container spacing={1}>
                      {movie.crew.map((c) => (
                        <Grid item xs={6} sm={6} md={4} key={c.person_id}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box component="img"
                              src={c.profile_url || '/image/no_image.png'}
                              alt={c.name}
                              sx={{ width: 32, height: 44, objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }}
                            />
                            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                              <Typography variant='body2' component="span" noWrap sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#ffffff', lineHeight: 1.3 }}>
                                {c.name}
                              </Typography>
                              <Typography variant='caption' component="span" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.2 }}>
                                {c.jobs.map(translateJob).join(', ')}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      準備中
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>

            {/* Right Column - Stats Sidebar */}
            <Grid item xs={12} md={4}>
              <Box>
                {/* Movie Info */}
                {(movie.original_language || (movie.budget && movie.budget > 0) || (movie.revenue && movie.revenue > 0)) && (
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {movie.original_language && (
                      <>
                        <Grid item xs={6}>
                          <Typography sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>オリジナル言語</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: '0.675rem', color: '#ffffff' }}>
                            {languageMap[movie.original_language] || movie.original_language.toUpperCase()}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {movie.budget !== undefined && movie.budget > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>製作費</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: '0.675rem', color: '#ffffff' }}>
                            {formatCurrency(movie.budget)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {movie.revenue !== undefined && movie.revenue > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>興行収入</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: '0.675rem', color: '#ffffff' }}>
                            {formatCurrency(movie.revenue)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}

                {/* External Links (superuser only) */}
                {user?.is_superuser && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {movie.homepage && (
                      <Button
                        variant="contained" size="small" fullWidth
                        component="a" href={movie.homepage}
                        target="_blank" rel="noopener noreferrer"
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          textTransform: 'none', fontSize: '0.7rem', color: '#ffffffe8',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                        }}
                      >
                        公式サイト
                      </Button>
                    )}
                    {movie.imdb_id && (
                      <Button
                        variant="contained" size="small" fullWidth
                        component="a" href={`https://www.imdb.com/title/${movie.imdb_id}`}
                        target="_blank" rel="noopener noreferrer"
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          textTransform: 'none', fontSize: '0.7rem', color: '#ffffffe8',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                        }}
                      >
                        IMDb
                      </Button>
                    )}
                    <Button
                      variant="contained" size="small" fullWidth
                      component="a" href={`https://www.themoviedb.org/movie/${movie.movie_id}`}
                      target="_blank" rel="noopener noreferrer"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        textTransform: 'none', fontSize: '0.7rem', color: '#ffffffe8',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                      }}
                    >
                      TMDB
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </NormalSection>

      {/* Wikipedia Viewer */}
      <WikipediaViewer
        open={wikiOpen}
        onClose={() => setWikiOpen(false)}
        title={movie.japanese_name || movie.title}
      />

      {/* Youtube Viewer */}
      <YoutubeViewer
        open={!!selectedVideoId}
        onClose={() => setSelectedVideoId(null)}
        videoId={selectedVideoId || ''}
      />

      {/* Review Dialog */}
      {openReviewDialog && (
        <MovieReviewDialog
          open={openReviewDialog}
          onClose={() => setOpenReviewDialog(false)}
          movieId={movie.movie_id}
          initialData={reviewData}
          releaseDate={movie.release_date}
          onReviewSaved={(data) => {
            setReviewData(data);
            setOpenReviewDialog(false);
          }}
        />
      )}
    </>
  );
};

export default memo(MovieDetailsPage);
